import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from '../../utils/axiosConfig';
import { logout } from '../../redux/actions/userActions';
import styles from './AIChatWidget.module.css';
import { serializeSavedChat } from './history';

const LEGACY_AUTOMATIC_GREETING = 'Hi, I am Watcher AI. Ask me about alerts, assets, reports, settings, traffic, or where to look next.';
const INITIAL_MESSAGES = [];

const MAX_HISTORY_MESSAGES = 20;
const MAX_SAVED_CHATS = 50;
const CHAT_STORAGE_PREFIX = 'watcher_ai_recent_chats_v1';
const CHAT_TITLE_MAX_LENGTH = 58;
const LONG_WAIT_MESSAGE_DELAY_MS = 7000;
const EXTENDED_WAIT_MESSAGE_DELAY_MS = 18000;
const STATUS_POLL_INTERVAL_MS = 30000;
const REPORT_POLL_INTERVAL_MS = 5000;
const REPORT_POLL_MAX_ATTEMPTS = 120;
const CHAT_POSITION_PADDING = 12;
const STREAM_RENDER_MAX_WAIT_MS = 50;

const createChatId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const cloneInitialMessages = () => INITIAL_MESSAGES.map((message) => ({ ...message }));

const getSessionChatKey = (userInfo) => {
	const session = userInfo?.session || {};
	if (!session.loggedin) return null;
	if (session.id && session.company_id) return `account:${session.id}:company:${session.company_id}`;

	const company = String(session.company || userInfo?.company || 'unknown-company').trim() || 'unknown-company';
	const role = String(session.role || userInfo?.role || 'unknown-role').trim() || 'unknown-role';
	const identity = String(
		session.username ||
		session.user ||
		session.email ||
		session.user_id ||
		session.account_id ||
		session.id ||
		userInfo?.username ||
		'user'
	).trim();

	return JSON.stringify([company, role, identity || 'user']);
};

const getChatStorageKey = (userKey) => userKey ? `${CHAT_STORAGE_PREFIX}:${userKey}` : null;

const normalizeChatMessage = (message) => {
	if (!message || typeof message.content !== 'string') return null;
	const role = message.role === 'user' ? 'user' : 'assistant';
	if (role === 'assistant' && message.content.trim() === LEGACY_AUTOMATIC_GREETING) return null;
	const normalized = { role, content: message.content };
	if (message.isError) {
		normalized.isError = true;
	}
	if (message.reportGeneration && typeof message.reportGeneration === 'object') {
		normalized.reportGeneration = { ...message.reportGeneration };
	}
	if (message.reportDownload && typeof message.reportDownload === 'object') {
		normalized.reportDownload = { ...message.reportDownload };
	}
	return normalized;
};

const normalizeChatMessages = (messages) => {
	const normalized = Array.isArray(messages)
		? messages.map(normalizeChatMessage).filter(Boolean)
		: [];
	return normalized.length ? normalized : cloneInitialMessages();
};

const hasUserMessage = (messages) => Array.isArray(messages) && messages.some((message) => message.role === 'user' && message.content?.trim());

const getChatTitle = (messages) => {
	const firstUserMessage = Array.isArray(messages)
		? messages.find((message) => message.role === 'user' && message.content?.trim())
		: null;
	const title = (firstUserMessage?.content || 'New chat').replace(/\s+/g, ' ').trim();
	return title.length > CHAT_TITLE_MAX_LENGTH ? `${title.slice(0, CHAT_TITLE_MAX_LENGTH - 1)}...` : title;
};

const sortAndLimitChats = (chats) => {
	const seen = new Set();
	return (Array.isArray(chats) ? chats : [])
		.slice()
		.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
		.filter((chat) => chat?.id && !seen.has(chat.id) && seen.add(chat.id))
		.slice(0, MAX_SAVED_CHATS);
};

const readSavedChats = (userKey) => {
	const storageKey = getChatStorageKey(userKey);
	if (!storageKey || typeof window === 'undefined') return [];
	try {
		const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
		return sortAndLimitChats(parsed.map((chat) => ({
			id: String(chat.id || createChatId()),
			title: chat.title || getChatTitle(chat.messages),
			messages: normalizeChatMessages(chat.messages),
			updatedAt: Number(chat.updatedAt) || 0,
		})));
	} catch (error) {
		return [];
	}
};

const writeSavedChats = (userKey, chats) => {
	const storageKey = getChatStorageKey(userKey);
	if (!storageKey || typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(storageKey, JSON.stringify(sortAndLimitChats(chats)));
	} catch (error) {
		// Browser storage may be unavailable or full; chat should still work without saved history.
	}
};

const buildSavedChat = (id, messages) => ({
	id: id || createChatId(),
	title: getChatTitle(messages),
	messages: normalizeChatMessages(messages),
	updatedAt: Date.now(),
});

const formatChatTime = (updatedAt) => {
	if (!updatedAt) return '';
	try {
		return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(updatedAt));
	} catch (error) {
		return '';
	}
};

const normalizeReportGeneration = (value) => {
	if (!value || typeof value !== 'object') return null;
	const requestId = String(value.request_id || value.requestId || '').trim();
	const startDate = String(value.start_date || value.startDate || '').trim();
	const endDate = String(value.end_date || value.endDate || '').trim();
	if (!requestId || !startDate || !endDate) return null;
	return {
		requestId,
		startDate,
		endDate,
		watcherFilter: String(value.watcher_filter || value.watcherFilter || '').trim(),
		watcherLabel: String(value.watcher_label || value.watcherLabel || 'selected watchers').trim(),
		generatedAfter: String(value.generated_after || value.generatedAfter || '').trim(),
		status: value.status || 'pending',
	};
};

const reportDownloadUrl = (reportId) => `/api/security-reports/download/${reportId}`;

const reportMatchesGeneration = (report, generation) => {
	if (!report || !generation) return false;
	const status = String(report.status || '').toLowerCase();
	if (status && status !== 'generated' && status !== 'sent') {
		return false;
	}
	if (generation.requestId && String(report.filePath || '').includes(generation.requestId)) {
		return Boolean(report.reportId);
	}
	if (String(report.startDate || '') !== generation.startDate || String(report.endDate || '') !== generation.endDate) {
		return false;
	}
	if (generation.generatedAfter && report.generatedDate) {
		const reportTime = Date.parse(String(report.generatedDate).replace(' ', 'T'));
		const startTime = Date.parse(generation.generatedAfter);
		if (Number.isFinite(reportTime) && Number.isFinite(startTime) && reportTime + 10000 < startTime) {
			return false;
		}
	}
	return Boolean(report.reportId);
};

const renderInlineText = (text, keyPrefix) => {
	const parts = String(text || '').split(/(`[^`]+`|\*\*[^*]+?\*\*)/g);
	return parts.map((part, index) => {
		if (!part) return null;
		const key = `${keyPrefix}-${index}`;
		if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
			return <code key={key}>{part.slice(1, -1)}</code>;
		}
		if (part.startsWith('**') && part.endsWith('**') && part.length > 3) {
			return <strong key={key}>{part.slice(2, -2)}</strong>;
		}
		return <React.Fragment key={key}>{part}</React.Fragment>;
	});
};

const FormattedMessage = ({ content }) => {
	const lines = String(content || '').split(/\r?\n/);
	const blocks = [];
	let index = 0;

	const isListLine = (line) => /^\s*(?:[-*]|\d+\.)\s+/.test(line);
	const isQuoteLine = (line) => /^\s*>\s?/.test(line);
	const listLineMatch = (line) => line.match(/^\s*(?:(\d+)\.|[-*])\s+(.+)$/);

	while (index < lines.length) {
		const line = lines[index];
		if (!line.trim()) {
			index += 1;
			continue;
		}

		if (isQuoteLine(line)) {
			const quoteLines = [];
			while (index < lines.length && isQuoteLine(lines[index])) {
				quoteLines.push(lines[index].replace(/^\s*>\s?/, ''));
				index += 1;
			}
			blocks.push(
				<blockquote key={`quote-${blocks.length}`}>
					{quoteLines.map((quoteLine, quoteIndex) => (
						<p key={`quote-line-${quoteIndex}`}>{renderInlineText(quoteLine, `quote-${blocks.length}-${quoteIndex}`)}</p>
					))}
				</blockquote>
			);
			continue;
		}

		if (isListLine(line)) {
			const ordered = /^\s*\d+\.\s+/.test(line);
			const items = [];
			while (index < lines.length && isListLine(lines[index]) && (/^\s*\d+\.\s+/.test(lines[index]) === ordered)) {
				const match = listLineMatch(lines[index]);
				if (match?.[2]) {
					items.push(match[2]);
				}
				index += 1;
			}
			const ListTag = ordered ? 'ol' : 'ul';
			blocks.push(
				<ListTag key={`list-${blocks.length}`}>
					{items.map((item, itemIndex) => (
						<li key={`list-item-${itemIndex}`}>{renderInlineText(item, `list-${blocks.length}-${itemIndex}`)}</li>
					))}
				</ListTag>
			);
			continue;
		}

		const paragraphLines = [];
		while (index < lines.length && lines[index].trim() && !isQuoteLine(lines[index]) && !isListLine(lines[index])) {
			paragraphLines.push(lines[index]);
			index += 1;
		}
		blocks.push(
			<p key={`paragraph-${blocks.length}`}>
				{paragraphLines.map((paragraphLine, lineIndex) => (
					<React.Fragment key={`paragraph-line-${lineIndex}`}>
						{lineIndex > 0 && <br />}
						{renderInlineText(paragraphLine, `paragraph-${blocks.length}-${lineIndex}`)}
					</React.Fragment>
				))}
			</p>
		);
	}

	return <div className={styles.messageContent}>{blocks}</div>;
};

const ChatIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path className={styles.chatIconBubble} d='M4 5.5C4 4.12 5.12 3 6.5 3h11C18.88 3 20 4.12 20 5.5v7C20 13.88 18.88 15 17.5 15H9.2L5.6 18.1C4.96 18.65 4 18.2 4 17.36V5.5Z' />
		<path d='M8 8h8M8 11h5' />
	</svg>
);

const CloseIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path d='M6 6l12 12M18 6L6 18' />
	</svg>
);

const MinimizeIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path d='M6 12h12' />
	</svg>
);

const ExpandIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path d='M8 3H3v5' />
		<path d='M3 3l6 6' />
		<path d='M16 3h5v5' />
		<path d='M21 3l-6 6' />
		<path d='M8 21H3v-5' />
		<path d='M3 21l6-6' />
		<path d='M16 21h5v-5' />
		<path d='M21 21l-6-6' />
	</svg>
);

const CollapseIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path d='M9 3v6H3' />
		<path d='M9 9L3 3' />
		<path d='M15 3v6h6' />
		<path d='M15 9l6-6' />
		<path d='M9 21v-6H3' />
		<path d='M9 15l-6 6' />
		<path d='M15 21v-6h6' />
		<path d='M15 15l6 6' />
	</svg>
);

const SendIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path d='M4 12l16-8-4 16-4-6-8-2Z' />
		<path d='M12 14l8-10' />
	</svg>
);

const StopIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path d='M8 8h8v8H8z' />
	</svg>
);

const ResetIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path d='M19 6.5v4.8h-4.8' />
		<path d='M18.2 11.3a6.2 6.2 0 1 0-2.1 4.65' />
	</svg>
);

const HistoryIcon = () => (
	<svg viewBox='0 0 24 24' aria-hidden='true' focusable='false'>
		<path d='M12 8v4l2.7 1.7' />
		<path d='M19 12a7 7 0 1 1-2.05-4.95' />
		<path d='M19 4v4h-4' />
	</svg>
);

const ThinkingMessage = ({ statusText }) => (
	<div className={`${styles.message} ${styles.assistantMessage} ${styles.thinkingMessage}`} role='status' aria-live='polite'>
		<span className={styles.thinkingText}>{statusText}</span>
		<span className={styles.thinkingDots} aria-hidden='true'>
			<span />
			<span />
			<span />
		</span>
	</div>
);

const getErrorMessage = (error) => {
	const responseMessage = error?.response?.data?.msg || error?.response?.data?.detail;
	if (typeof responseMessage === 'string') {
		return responseMessage.replace(/^\d+\s-\s[a-zA-Z0-9_]+\s-\s/, '');
	}
	return 'Watcher AI is not available right now. Please try again later or contact Frontline for additional support.';
};

const isCanceledRequest = (error) => error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || axios.isCancel?.(error);

const getFetchErrorMessage = async (response) => {
			try {
		const payload = await response.json();
		const message = payload?.msg || payload?.detail;
		if (typeof message === 'string') {
			return message.replace(/^\d+\s-\s[a-zA-Z0-9_]+\s-\s/, '');
		}
	} catch (error) {
		// The streaming endpoint may return a plain response if setup fails.
	}
	return 'Watcher AI is not available right now. Please try again later or contact Frontline for additional support.';
};

const parseStreamEvent = (block) => {
	let event = 'message';
	const dataLines = [];
	block.split(/\r?\n/).forEach((line) => {
		if (line.startsWith('event:')) {
			event = line.slice(6).trim();
		} else if (line.startsWith('data:')) {
			dataLines.push(line.slice(5).trimStart());
		}
	});
	if (!dataLines.length) return null;
	try {
		return { event, data: JSON.parse(dataLines.join('\n')) };
	} catch (error) {
		return { event, data: { token: dataLines.join('\n') } };
	}
};

const readEventStream = async (response, onEvent) => {
	const reader = response.body?.getReader();
	if (!reader) {
		throw new Error('Streaming is not supported by this browser.');
	}
	const decoder = new TextDecoder();
	let buffer = '';
	while (true) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		const blocks = buffer.split(/\n\n/);
		buffer = blocks.pop() || '';
		blocks.forEach((block) => {
			const parsed = parseStreamEvent(block.trim());
			if (parsed) onEvent(parsed);
		});
	}
	if (buffer.trim()) {
		const parsed = parseStreamEvent(buffer.trim());
		if (parsed) onEvent(parsed);
	}
};

const AIChatWidget = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const userLogin = useSelector((state) => state.userLogin);
	const isLoggedIn = Boolean(userLogin?.userInfo?.session?.loggedin);
	const isSessionOTLicensed = Boolean(userLogin?.userInfo?.session?.ot_licensed);
	const currentUserKey = React.useMemo(() => (isLoggedIn ? getSessionChatKey(userLogin?.userInfo) : null), [isLoggedIn, userLogin?.userInfo]);

	const [isOpen, setIsOpen] = React.useState(false);
	const [messages, setMessages] = React.useState(cloneInitialMessages);
	const [savedChats, setSavedChats] = React.useState([]);
	const [historyNotice, setHistoryNotice] = React.useState('');
	const [activeChatId, setActiveChatId] = React.useState(createChatId);
	const [draft, setDraft] = React.useState('');
	const [historySearch, setHistorySearch] = React.useState('');
	const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
	const [isExpanded, setIsExpanded] = React.useState(false);
	const [isSending, setIsSending] = React.useState(false);
	const [streamingContent, setStreamingContent] = React.useState(false);
	const [pendingStatus, setPendingStatus] = React.useState('Thinking through this');
	const [status, setStatus] = React.useState(null);
	const [serverOTLicensed, setServerOTLicensed] = React.useState(null);
	const [learningBusy, setLearningBusy] = React.useState(false);
	const [aiBusy, setAiBusy] = React.useState(false);
	const [modeBusy, setModeBusy] = React.useState(false);
	const toggleRef = React.useRef(null);
	const suppressToggleClickRef = React.useRef(false);
	const messagesEndRef = React.useRef(null);
	const inputRef = React.useRef(null);
	const abortControllerRef = React.useRef(null);
	const lastUserKeyRef = React.useRef(null);
	const historySyncRef = React.useRef(Promise.resolve());
	const historyWriteRef = React.useRef(Promise.resolve());
	const deletedChatIdsRef = React.useRef(new Set());
	const skipNextSaveRef = React.useRef(false);
	const reportPollsRef = React.useRef({});
	const statusPollInFlightRef = React.useRef(false);
	const effectiveOTLicensed = serverOTLicensed === null ? isSessionOTLicensed : serverOTLicensed;
	const shouldHide = !isLoggedIn || !effectiveOTLicensed || location.pathname === '/login' || location.pathname === '/mfa-setup';

	const abortActiveRequest = React.useCallback(() => {
		abortControllerRef.current?.abort();
	}, []);

	React.useEffect(() => () => {
		Object.values(reportPollsRef.current).forEach((intervalId) => window.clearInterval(intervalId));
		reportPollsRef.current = {};
	}, []);

	React.useEffect(() => {
		if (!isLoggedIn) {
			setServerOTLicensed(null);
			return undefined;
		}

		let cancelled = false;
		const loadLicenseStatus = async () => {
			try {
				const response = await axios.get('/api/ai-chat/status', { params: { license_only: 1 } });
				const nextLicensed = Boolean(response.data?.ot_licensed);
				if (!cancelled) {
					setServerOTLicensed(nextLicensed);
					if (!nextLicensed) {
						setIsOpen(false);
					}
				}
			} catch (error) {
				if (!cancelled) {
					if (error?.response?.status === 403) {
						setServerOTLicensed(false);
						setIsOpen(false);
					} else {
						setServerOTLicensed(null);
					}
				}
			}
		};

		loadLicenseStatus();
		return () => {
			cancelled = true;
		};
	}, [currentUserKey, isLoggedIn, isSessionOTLicensed]);

	React.useEffect(() => {
		if (!isOpen || shouldHide) return undefined;

		let cancelled = false;
		const loadStatus = async () => {
			if (statusPollInFlightRef.current) return;
			statusPollInFlightRef.current = true;
			try {
				const response = await axios.get('/api/ai-chat/status');
				if (!cancelled) {
					if (response.data?.ot_licensed === false) {
						setServerOTLicensed(false);
						setIsOpen(false);
						return;
					}
					setStatus(response.data);
				}
			} catch (error) {
				if (!cancelled) {
					if (error?.response?.status === 403) {
						setServerOTLicensed(false);
						setIsOpen(false);
						return;
					}
					setStatus({
						status: false,
						connected: false,
						responsive: false,
						msg: getErrorMessage(error),
					});
				}
			} finally {
				statusPollInFlightRef.current = false;
			}
		};

		loadStatus();
		const intervalId = window.setInterval(() => {
			loadStatus();
		}, STATUS_POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			window.clearInterval(intervalId);
		};
	}, [isOpen, shouldHide]);

	React.useEffect(() => {
		if (!isOpen) return;
		messagesEndRef.current?.scrollIntoView({ behavior: isSending ? 'auto' : 'smooth', block: 'end' });
	}, [messages, isOpen, pendingStatus, isSending]);

	React.useEffect(() => {
		if (!isOpen) return;
		window.setTimeout(() => inputRef.current?.focus(), 120);
		}, [isOpen, isExpanded]);

	React.useEffect(() => {
		if (!isSending) {
			setPendingStatus('Thinking through this');
			setStreamingContent(false);
			return undefined;
		}

		setPendingStatus('Thinking through this');
		const longWaitTimer = window.setTimeout(() => {
			setPendingStatus('Still searching Watcher data');
		}, LONG_WAIT_MESSAGE_DELAY_MS);
		const extendedWaitTimer = window.setTimeout(() => {
			setPendingStatus('Still working. The local model is taking a little longer');
		}, EXTENDED_WAIT_MESSAGE_DELAY_MS);

		return () => {
			window.clearTimeout(longWaitTimer);
			window.clearTimeout(extendedWaitTimer);
		};
	}, [isSending]);

	React.useEffect(() => () => {
		abortActiveRequest();
		abortControllerRef.current = null;
		lastUserKeyRef.current = null;
	}, [abortActiveRequest]);

	React.useEffect(() => {
		setHistoryNotice('');
		deletedChatIdsRef.current = new Set();
		if (!currentUserKey) {
			abortActiveRequest();
			abortControllerRef.current = null;
			lastUserKeyRef.current = null;
			skipNextSaveRef.current = true;
			setSavedChats([]);
			setActiveChatId(createChatId());
			setMessages(cloneInitialMessages());
			setDraft('');
			setHistorySearch('');
			setIsHistoryOpen(false);
			setIsSending(false);
			setStreamingContent(false);
			return;
		}

		if (lastUserKeyRef.current === currentUserKey) return;

		abortActiveRequest();
		abortControllerRef.current = null;
		lastUserKeyRef.current = currentUserKey;
		skipNextSaveRef.current = true;
		setSavedChats(readSavedChats(currentUserKey));
		setActiveChatId(createChatId());
		setMessages(cloneInitialMessages());
		setDraft('');
		setHistorySearch('');
		setIsHistoryOpen(false);
		setIsSending(false);
		setStreamingContent(false);
		setPendingStatus('Thinking through this');
	}, [abortActiveRequest, currentUserKey]);

	React.useEffect(() => {
		if (!currentUserKey || !effectiveOTLicensed) return undefined;
		const controller = new AbortController();
		let cancelled = false;
		const importKey = `${getChatStorageKey(currentUserKey)}:server_imported`;
		historySyncRef.current = (async () => {
			let imported = false;
			try { imported = window.localStorage.getItem(importKey) === 'true'; } catch (error) { /* Storage is optional. */ }
			let response = await axios.get('/api/ai-chat/history', { signal: controller.signal });
			if (cancelled || lastUserKeyRef.current !== currentUserKey) return;
			if (!imported) {
				const legacyChats = response.data.legacy_import_allowed ? readSavedChats(response.data.legacy_storage_key) : [];
				const chats = sortAndLimitChats([...readSavedChats(currentUserKey), ...legacyChats]).map(serializeSavedChat).filter(Boolean);
				if (chats.length) {
					await axios.post('/api/ai-chat/history/import', { chats }, { signal: controller.signal });
					response = await axios.get('/api/ai-chat/history', { signal: controller.signal });
				}
			}
			if (cancelled || lastUserKeyRef.current !== currentUserKey) return;
			const chats = sortAndLimitChats(response.data.chats || []);
			setSavedChats(chats);
			writeSavedChats(currentUserKey, chats);
			try { window.localStorage.setItem(importKey, 'true'); } catch (error) { /* Storage is optional. */ }
			setHistoryNotice('');
		})().catch((error) => {
			if (!cancelled && !isCanceledRequest(error)) setHistoryNotice('Saved chats could not sync. Reopen the page to try again.');
		});
		return () => { cancelled = true; controller.abort(); };
	}, [currentUserKey, effectiveOTLicensed]);

	React.useEffect(() => {
		if (skipNextSaveRef.current) {
			skipNextSaveRef.current = false;
			return;
		}
		if (!currentUserKey || !hasUserMessage(messages)) return;

		const nextChat = buildSavedChat(activeChatId, messages);
		setSavedChats((current) => {
			const nextChats = sortAndLimitChats([nextChat, ...current.filter((chat) => chat.id !== nextChat.id)]);
			writeSavedChats(currentUserKey, nextChats);
			return nextChats;
		});
	}, [activeChatId, currentUserKey, messages]);

	React.useEffect(() => {
		if (!currentUserKey || !effectiveOTLicensed || isSending || !hasUserMessage(messages)) return;
		const chat = serializeSavedChat(buildSavedChat(activeChatId, messages));
		if (!chat) return;
		const ready = historySyncRef.current;
		historyWriteRef.current = historyWriteRef.current.then(async () => {
			await ready;
			if (lastUserKeyRef.current !== currentUserKey || deletedChatIdsRef.current.has(chat.id)) return;
			await axios.put(`/api/ai-chat/history/${encodeURIComponent(chat.id)}`, chat);
		}).catch(() => {
			if (lastUserKeyRef.current === currentUserKey) setHistoryNotice('Saved chats could not sync. Reopen the page to try again.');
		});
	}, [activeChatId, currentUserKey, effectiveOTLicensed, isSending, messages]);

	if (shouldHide) {
		return null;
	}

	const aiDisabled = status && status.enabled === false;
	const canViewAiDiagnostics = Boolean(status?.diagnostics_visible);
	const watcherAiWarming = Boolean(status?.warming_up);
	const canUseMode = Boolean(status?.mode_available ?? status?.mode_admin);
	const watcherAiConnected = Boolean(status?.watcher_ai_connected ?? (status?.connected && status?.responsive !== false && !aiDisabled));
	const connectionStatusText = !status
		? 'Checking Watcher AI'
		: aiDisabled
			? 'Watcher AI is off'
			: canViewAiDiagnostics
				? watcherAiWarming
					? 'Watcher AI warming up'
					: status?.connected && status?.responsive === false
						? 'Ollama connected; model not responding'
						: status?.connected
							? 'Connected to Watcher AI'
							: 'Local Ollama assistant'
				: watcherAiWarming
					? 'Starting Watcher AI'
					: watcherAiConnected
						? 'Connected to Watcher AI'
						: 'Disconnected from Watcher AI';
	const userChatReady = Boolean(currentUserKey && lastUserKeyRef.current === currentUserKey);
	const visibleMessages = userChatReady ? messages : cloneInitialMessages();
	const visibleSavedChats = userChatReady ? savedChats : [];
	const filteredSavedChats = visibleSavedChats.filter((chat) => {
		const searchText = historySearch.trim().toLowerCase();
		if (!searchText) return true;
		const haystack = [
			chat.title,
			formatChatTime(chat.updatedAt),
			...(Array.isArray(chat.messages) ? chat.messages.map((message) => message.content) : []),
		].join(' ').toLowerCase();
		return haystack.includes(searchText);
	});

	const closeChat = () => {
		setIsOpen(false);
		setIsExpanded(false);
	};

	const toggleChat = () => {
		if (suppressToggleClickRef.current) {
			suppressToggleClickRef.current = false;
			return;
		}
		if (isOpen) {
			closeChat();
			return;
		}
		setIsOpen(true);
	};

	const toggleExpanded = () => {
		setIsExpanded((current) => !current);
		setIsHistoryOpen(false);
	};

	const updateReportMessage = (requestId, updater) => {
		if (!requestId || typeof updater !== 'function') return;
		setMessages((current) => current.map((message) => {
			const generationId = message.reportGeneration?.requestId;
			const downloadId = message.reportDownload?.requestId;
			if (generationId !== requestId && downloadId !== requestId) {
				return message;
			}
			return {
				...message,
				...updater(message),
			};
		}));
	};

	const markReportDownloading = (requestId) => {
		if (!requestId) return;
		updateReportMessage(requestId, (message) => {
			const notice = 'Downloading the PDF now.';
			return {
				content: message.content.includes(notice) ? message.content : `${message.content}\n\n${notice}`,
				reportGeneration: {
					...(message.reportGeneration || {}),
					requestId,
					status: 'downloading',
				},
			};
		});
	};

	const pollReportGeneration = (generation) => {
		if (!generation?.requestId || reportPollsRef.current[generation.requestId]) return;

		let attempts = 0;
		const stopPolling = () => {
			const intervalId = reportPollsRef.current[generation.requestId];
			if (intervalId) {
				window.clearInterval(intervalId);
				delete reportPollsRef.current[generation.requestId];
			}
		};

		const poll = async () => {
			attempts += 1;
			try {
				const response = await axios.get('/api/security-reports/history');
				const reports = Array.isArray(response.data?.reports) ? response.data.reports : [];
				const matchingReport = reports.find((report) => reportMatchesGeneration(report, generation));
				if (matchingReport) {
					stopPolling();
					const title = matchingReport.reportTitle || `Security Report ${generation.startDate} to ${generation.endDate}`;
					updateReportMessage(generation.requestId, () => ({
						content: `Your Security Report is ready to download.\n\n${title}`,
						reportGeneration: {
							...generation,
							status: 'ready',
						},
						reportDownload: {
							requestId: generation.requestId,
							reportId: matchingReport.reportId,
							title,
							url: reportDownloadUrl(matchingReport.reportId),
						},
					}));
					return;
				}
				if (attempts >= REPORT_POLL_MAX_ATTEMPTS) {
					stopPolling();
					updateReportMessage(generation.requestId, (message) => ({
						content: `${message.content}\n\nThe PDF is still generating. You can also check Reports > Security Reports for it.`,
						reportGeneration: {
							...generation,
							status: 'timeout',
						},
					}));
				}
			} catch (error) {
				if (attempts >= REPORT_POLL_MAX_ATTEMPTS) {
					stopPolling();
					updateReportMessage(generation.requestId, (message) => ({
						content: `${message.content}\n\nI could not confirm the download from chat. Check Reports > Security Reports for the finished PDF.`,
						reportGeneration: {
							...generation,
							status: 'error',
						},
					}));
				}
			}
		};

		reportPollsRef.current[generation.requestId] = window.setInterval(poll, REPORT_POLL_INTERVAL_MS);
		poll();
	};

	const sendMessage = async () => {
		const trimmedDraft = draft.trim();
		if (!trimmedDraft || isSending || aiDisabled || !userChatReady) return;

		const userMessage = { role: 'user', content: trimmedDraft };
		const nextMessages = [...messages, userMessage];
		const history = messages
			.filter((message) => !message.isError && (message.role === 'user' || message.role === 'assistant'))
			.slice(-MAX_HISTORY_MESSAGES)
			.map(({ role, content }) => ({ role, content: content.slice(-8000) }));
		while (history.reduce((length, message) => length + message.content.length, 0) > 16000) {
			history.shift();
		}

		const controller = new AbortController();
		abortControllerRef.current = controller;
		setMessages(nextMessages);
		setDraft('');
		setIsSending(true);
		setStreamingContent(false);
		let streamRenderTimer;

		try {
			await Promise.all([historySyncRef.current, historyWriteRef.current]);
			if (controller.signal.aborted) throw new DOMException('Request canceled', 'AbortError');
			const response = await fetch('/api/ai-chat/stream', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				credentials: 'same-origin',
				signal: controller.signal,
				body: JSON.stringify({
					message: trimmedDraft,
					history,
					page: location.pathname,
					chat_id: activeChatId,
				}),
			});

			if (!response.ok) {
				throw new Error(await getFetchErrorMessage(response));
			}

				let streamedReply = '';
				let renderedReply = '';
				let assistantStarted = false;
				let donePayload = null;
				const updateAssistant = (content, isError = false, extra = {}) => {
					if (abortControllerRef.current !== controller) return;
					assistantStarted = true;
					setMessages((current) => {
					const hasAssistant = current.length > nextMessages.length && current[current.length - 1]?.role === 'assistant';
					const assistantMessage = { role: 'assistant', content, isError, ...extra };
					if (hasAssistant) {
						return [...current.slice(0, -1), assistantMessage];
					}
						return [...nextMessages, assistantMessage];
					});
				};
				const flushStreamedReply = () => {
					streamRenderTimer = undefined;
					if (abortControllerRef.current !== controller || controller.signal.aborted) return;
					if (!streamedReply || streamedReply === renderedReply) return;
					renderedReply = streamedReply;
					setStreamingContent(true);
					updateAssistant(renderedReply);
				};

				await readEventStream(response, ({ event, data }) => {
					if (event === 'token') {
						streamedReply += data?.token || '';
						if (!renderedReply) flushStreamedReply();
						else if (!streamRenderTimer) streamRenderTimer = window.setTimeout(flushStreamedReply, STREAM_RENDER_MAX_WAIT_MS);
					} else if (event === 'done') {
						donePayload = data;
					} else if (event === 'error') {
					throw new Error(data?.msg || 'Watcher AI could not complete the request.');
				}
				});
				window.clearTimeout(streamRenderTimer);
				if (abortControllerRef.current !== controller) return;

				const reply = donePayload?.reply || streamedReply || 'I did not get a usable response from the local model.';
				const reportGeneration = normalizeReportGeneration(donePayload?.report_generation || donePayload?.reportGeneration);
				const assistantExtras = reportGeneration ? { reportGeneration } : {};
				if (reply === streamedReply) {
					flushStreamedReply();
				}
				if (!assistantStarted || reply !== renderedReply) {
					updateAssistant(reply, false, assistantExtras);
				} else if (reportGeneration) {
					updateAssistant(reply, false, assistantExtras);
				}
				if (reportGeneration) {
					pollReportGeneration(reportGeneration);
				}
			if (donePayload?.navigate_to) {
				navigate(donePayload.navigate_to);
			}
			if (donePayload?.action === 'logout') {
				await dispatch(logout());
				setIsOpen(false);
				navigate('/login');
			}
		} catch (error) {
			if (abortControllerRef.current !== controller) return;
			if (isCanceledRequest(error)) {
				setMessages((current) => (current.length > nextMessages.length ? [...current, { role: 'assistant', content: 'Canceled that request.' }] : [...nextMessages, { role: 'assistant', content: 'Canceled that request.' }]));
			} else {
				setMessages([
					...nextMessages,
					{
						role: 'assistant',
						content: error?.message || getErrorMessage(error),
						isError: true,
					},
				]);
			}
		} finally {
			window.clearTimeout(streamRenderTimer);
			if (abortControllerRef.current === controller) {
				abortControllerRef.current = null;
				setIsSending(false);
				setStreamingContent(false);
			}
		}
	};

	const cancelRequest = () => {
		abortActiveRequest();
	};

	const handleKeyDown = (event) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			sendMessage();
		}
	};

	const startNewChat = () => {
		abortActiveRequest();
		abortControllerRef.current = null;
		setActiveChatId(createChatId());
		setMessages(cloneInitialMessages());
		setDraft('');
		setHistorySearch('');
		setIsHistoryOpen(false);
		setIsSending(false);
		setStreamingContent(false);
		inputRef.current?.focus();
	};

	const resetConversation = () => {
		startNewChat();
	};

	const openSavedChat = (chat) => {
		if (!chat || isSending) return;
		abortActiveRequest();
		abortControllerRef.current = null;
		setActiveChatId(chat.id);
		setMessages(normalizeChatMessages(chat.messages));
		setDraft('');
		setIsHistoryOpen(false);
		setStreamingContent(false);
		inputRef.current?.focus();
	};

	const deleteSavedChat = (chatId) => {
		if (!chatId || !currentUserKey) return;
		deletedChatIdsRef.current.add(chatId);
		const ready = historySyncRef.current;
		historyWriteRef.current = historyWriteRef.current.then(async () => {
			await ready;
			if (lastUserKeyRef.current !== currentUserKey) return;
			await axios.delete(`/api/ai-chat/history/${encodeURIComponent(chatId)}`);
		}).catch(() => {
			if (lastUserKeyRef.current === currentUserKey) setHistoryNotice('This chat could not be deleted from saved history. Reopen the page to retry.');
		});
		setSavedChats((current) => {
			const nextChats = current.filter((chat) => chat.id !== chatId);
			writeSavedChats(currentUserKey, nextChats);
			return nextChats;
		});
		if (chatId === activeChatId) {
			startNewChat();
		}
	};

	const toggleAI = async () => {
		if (!status?.ai_admin || aiBusy) return;
		const nextEnabled = !status?.enabled;
		setAiBusy(true);
		try {
			const response = await axios.post('/api/ai-chat/enabled', { enabled: nextEnabled });
			setStatus((current) => ({
				...current,
				enabled: response.data.enabled,
				ai_admin: response.data.ai_admin,
				learning: response.data.learning || current?.learning,
			}));
			setMessages((current) => ([
				...current,
				{ role: 'assistant', content: `Watcher AI is now ${response.data.enabled ? 'on' : 'off'}.` },
			]));
		} catch (error) {
			setMessages((current) => ([
				...current,
				{ role: 'assistant', content: getErrorMessage(error), isError: true },
			]));
		} finally {
			setAiBusy(false);
		}
	};

	const toggleLearning = async () => {
		if (!status?.learning_admin || learningBusy) return;
		const nextEnabled = !status?.learning?.enabled;
		setLearningBusy(true);
		try {
			const response = await axios.post('/api/ai-chat/learning', { enabled: nextEnabled });
			setStatus((current) => ({
				...current,
				learning: response.data.learning,
				learning_admin: response.data.learning_admin,
				ai_admin: response.data.ai_admin ?? current?.ai_admin,
				enabled: response.data.enabled ?? current?.enabled,
			}));
		} catch (error) {
			setMessages((current) => ([
				...current,
				{ role: 'assistant', content: getErrorMessage(error), isError: true },
			]));
		} finally {
			setLearningBusy(false);
		}
	};

	const toggleMode = async () => {
		if (!canUseMode || modeBusy) return;
		const currentMode = status?.ai_mode === 'deep' ? 'deep' : 'fast';
		const nextMode = currentMode === 'deep' ? 'fast' : 'deep';
		setModeBusy(true);
		try {
			const response = await axios.post('/api/ai-chat/mode', { mode: nextMode });
			setStatus((current) => ({
					...current,
					ai_mode: response.data.ai_mode,
					mode_available: response.data.mode_available ?? response.data.mode_admin,
					mode_admin: response.data.mode_admin,
					reasoning_enabled: response.data.reasoning_enabled,
				}));
			const modeLabel = response.data.ai_mode === 'deep' ? 'Deep' : 'Fast';
			setMessages((current) => ([
				...current,
				{ role: 'assistant', content: 'Watcher AI is now in ' + modeLabel + ' mode.' },
			]));
		} catch (error) {
			setMessages((current) => ([
				...current,
				{ role: 'assistant', content: getErrorMessage(error), isError: true },
			]));
		} finally {
			setModeBusy(false);
		}
	};

		return (
			<div
				className={[
					styles.chatRoot,
					isOpen && isExpanded ? styles.chatRootExpanded : '',
				].join(' ')}
				style={
					isOpen && isExpanded
						? { left: `${CHAT_POSITION_PADDING}px`, top: `${CHAT_POSITION_PADDING}px`, bottom: 'auto' }
						: undefined
				}
			>
				{isOpen && (
					<section className={`${styles.chatPanel} ${isExpanded ? styles.chatPanelExpanded : ''}`} aria-label='Watcher AI chat'>
						<header
							className={styles.header}
							title='Watcher AI'
						>
							<div>
								<h2>Watcher AI</h2>
								<p className={!aiDisabled && (watcherAiConnected || watcherAiWarming) ? styles.statusOnline : styles.statusOffline}>
									{connectionStatusText}
								</p>
							</div>
							<div className={styles.headerActions}>
								<button
									type='button'
									onClick={() => setIsHistoryOpen((current) => !current)}
									className={`${styles.iconButton} ${isHistoryOpen ? styles.iconButtonActive : ''}`}
									aria-label='Chat history'
									aria-pressed={isHistoryOpen}
									title='Chat history'
								>
									<HistoryIcon />
									{visibleSavedChats.length > 0 && <span className={styles.historyCount}>{visibleSavedChats.length}</span>}
								</button>
								<button
									type='button'
									onClick={toggleExpanded}
									className={`${styles.iconButton} ${isExpanded ? styles.iconButtonActive : ''}`}
									aria-label={isExpanded ? 'Shrink chat' : 'Expand chat'}
									aria-pressed={isExpanded}
									title={isExpanded ? 'Shrink chat' : 'Expand chat'}
								>
									{isExpanded ? <CollapseIcon /> : <ExpandIcon />}
								</button>
								<button type='button' onClick={resetConversation} className={`${styles.iconButton} ${styles.resetButton}`} aria-label='Reset chat'>
									<ResetIcon />
								</button>
								<button type='button' onClick={closeChat} className={`${styles.iconButton} ${styles.minimizeButton}`} aria-label='Minimize chat' title='Minimize chat'>
									<MinimizeIcon />
								</button>
							</div>
						</header>

						{canViewAiDiagnostics && status?.warming_up && (
							<div className={styles.notice}>
								Watcher AI is warming up. The model may still be loading on the Ollama server.
							</div>
						)}

						{canViewAiDiagnostics && status && !status.connected && !status.warming_up && (
							<div className={styles.notice}>
								Ollama is not connected. Run Ollama locally and pull the configured model before chatting.
							</div>
						)}

						{canViewAiDiagnostics && status?.connected && status?.responsive === false && !status?.warming_up && (
							<div className={styles.notice}>
								{status.msg || 'Ollama is reachable, but the configured model is not installed. Pull the configured model before chatting.'}
							</div>
						)}

					{(status?.ai_admin || status?.learning_admin || canUseMode) && (
						<div className={styles.adminControls}>
							{status?.ai_admin && (
								<div className={styles.adminToggle}>
									<span>AI</span>
									<button
										type='button'
										className={`${styles.switchButton} ${!aiDisabled ? styles.switchOn : ''}`}
										onClick={toggleAI}
										disabled={aiBusy}
										aria-label='Toggle Watcher AI'
										aria-pressed={!aiDisabled}
									>
										<span />
									</button>
								</div>
							)}
							{canUseMode && (
								<div className={[styles.adminToggle, styles.modeControl].join(" ")}>
									<span>Mode</span>
									<button
										type="button"
										className={[styles.modeButton, status?.ai_mode === "deep" ? styles.modeDeep : styles.modeFast].join(" ")}
										onClick={toggleMode}
										disabled={modeBusy}
										aria-label="Toggle Watcher AI fast or deep mode"
										aria-pressed={status?.ai_mode === "deep"}
									>
										<span>Fast</span>
										<span>Deep</span>
									</button>
								</div>
							)}
							{status?.learning_admin && (
								<div className={styles.adminToggle}>
									<span>Learning</span>
									<button
										type='button'
										className={`${styles.switchButton} ${status?.learning?.enabled ? styles.switchOn : ''}`}
										onClick={toggleLearning}
										disabled={learningBusy}
										aria-label='Toggle Watcher AI learning'
										aria-pressed={Boolean(status?.learning?.enabled)}
									>
										<span />
									</button>
								</div>
							)}
						</div>
					)}

						{aiDisabled && !status?.ai_admin && (
							<div className={styles.notice}>
							Watcher AI is currently off. Contact Frontline for additional support.
							</div>
						)}

					{isHistoryOpen && (
						<div className={styles.historyPanel} aria-label='Chat history'>
							<div className={styles.historyPanelHeader}>
								<div>
									<h3>History</h3>
									<p>{visibleSavedChats.length} of {MAX_SAVED_CHATS} saved · AI can reference your past chats</p>
									<p>When full, saving a new chat removes the least recently updated chat.</p>
								</div>
								<div className={styles.historyPanelActions}>
									<button type='button' onClick={startNewChat} disabled={isSending}>
										New
									</button>
									<button type='button' onClick={() => setIsHistoryOpen(false)}>
										Close
									</button>
								</div>
							</div>
							<input
								className={styles.historySearch}
								type='search'
								value={historySearch}
								onChange={(event) => setHistorySearch(event.target.value)}
								placeholder='Search history'
								aria-label='Search chat history'
							/>
							<div className={styles.historyList}>
								{filteredSavedChats.length > 0 ? (
									filteredSavedChats.map((chat) => (
										<div key={chat.id} className={`${styles.historyRow} ${chat.id === activeChatId ? styles.historyRowActive : ''}`}>
											<button type='button' className={styles.historyOpen} onClick={() => openSavedChat(chat)} disabled={isSending}>
												<span>{chat.title}</span>
												<small>{formatChatTime(chat.updatedAt)}</small>
											</button>
											<button type='button' className={styles.historyDelete} onClick={() => deleteSavedChat(chat.id)} aria-label={`Delete chat ${chat.title}`}>
												Delete
											</button>
										</div>
									))
								) : (
									<div className={styles.historyEmpty}>{historySearch.trim() ? 'No matches' : 'No saved chats'}</div>
								)}
							</div>
						</div>
					)}
					{historyNotice && <div className={styles.notice} role='status'>{historyNotice}</div>}

					<div className={styles.messages} aria-live='polite'>
						{visibleMessages.map((message, index) => (
							<div
								key={`${message.role}-${index}`}
								className={[
									styles.message,
									message.role === 'user' ? styles.userMessage : styles.assistantMessage,
									message.isError ? styles.errorMessage : '',
									].join(' ')}
								>
									<FormattedMessage content={message.content} />
									{message.reportGeneration?.status === 'pending' && (
										<div className={styles.reportStatus}>
										Preparing PDF...
									</div>
								)}
								{message.reportDownload?.url && (
									<a
										className={styles.reportDownload}
										href={message.reportDownload.url}
										onClick={() => markReportDownloading(message.reportDownload.requestId)}
									>
										Download PDF
									</a>
								)}
							</div>
						))}
						{isSending && !streamingContent && <ThinkingMessage statusText={pendingStatus} />}
						<div ref={messagesEndRef} />
					</div>

					<form
						className={styles.composer}
						onSubmit={(event) => {
							event.preventDefault();
							sendMessage();
						}}
					>
						<textarea
							ref={inputRef}
							value={draft}
							onChange={(event) => setDraft(event.target.value)}
							onKeyDown={handleKeyDown}
							placeholder={aiDisabled ? 'Watcher AI is off' : 'Ask Watcher AI'}
							rows={2}
							maxLength={2000}
							disabled={aiDisabled}
						/>
						{isSending ? (
							<button type='button' onClick={cancelRequest} className={styles.cancelButton} aria-label='Cancel request'>
								<StopIcon />
							</button>
						) : (
							<button type='submit' disabled={!draft.trim() || aiDisabled} aria-label='Send message'>
								<SendIcon />
							</button>
						)}
					</form>
				</section>
			)}

			<button
				ref={toggleRef}
				type='button'
				className={styles.chatToggle}
				onClick={toggleChat}
				aria-label={isOpen ? 'Close Watcher AI chat' : 'Open Watcher AI chat'}
				aria-expanded={isOpen}
			>
				{isOpen ? <CloseIcon /> : <ChatIcon />}
			</button>
		</div>
	);
};

export default AIChatWidget;
